import * as React from "react";
import {
  Button,
  Card,
  createStyles,
  DialogActions,
  DialogContentText,
  Grid,
  makeStyles,
  Theme,
} from "@material-ui/core";
import InstallationLevelReportHeader from "../../../components/InstallationLevelReportHeader";
import TankModel from "../../../core/models/Tank";
import InstallationTankLevelReportCard from "../../../components/InstallationTankLevelReportCard";
import get from "lodash/get";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { isDateValid } from "../../../helpers/datetime";
import { isAfter } from "date-fns/esm";
import { useHubx } from "../../../HubX";
import { POST_INSTALLATION_TANKS_READINGS } from "../../../Fedx";
import {
  InstallationLevelServicePutParams,
  putTankReading,
} from "../../../services/InstallationLevelService";
import { castNumberTo2Decimals, isNumberValid } from "../../../helpers/utility";
import { useSnackbar } from "notistack";
import SimpleDialog from "../../../components/SimpleDialog";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridRoot: {
      "padding": theme.spacing(4, 4, 1, 4),
      "rowGap": `${theme.spacing(4)}px`,
      "columnGap": theme.spacing(4),

      "& .MuiGrid-grid-xs-true": {
        flexBasis: "unset",
        flexGrow: 0,
      },

      [theme.breakpoints.down("sm")]: {
        padding: 0,
      },
    },

    dialogText: {
      padding: theme.spacing(2),
    },
  })
);

type InstallationLevelReportTabProps = {
  hasRTU: boolean;
  installationId?: string;
  tanks?: TankModel[];
  installationLastReading?: number;
};

function InstallationLevelReportTab({
  installationLastReading,
  hasRTU,
  tanks,
  installationId,
}: InstallationLevelReportTabProps) {
  const [useTankLastReadings, setTankLastReadings] = React.useState<Record<string, string>>({});
  const [useTravelBool, setTravelBool] = React.useState(false);
  const [useDate, setDate] = React.useState<Date | null>(null);
  const [postLoading, setPostLoading] = React.useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

  const hub = useHubx();
  const toastr = useSnackbar();
  const classes = useStyles();

  React.useEffect(() => {
    const nextState: Record<string, string> = {};
    tanks?.forEach((tank) => (nextState[tank.tank_id] = ""));

    setTankLastReadings(nextState);
    setTravelBool(false);
    setDate(null);
  }, [tanks]);

  const handleChangeTankReading = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTankLastReadings((prevState) => {
      return { ...prevState, [event.target.name]: Number(event.target.value).toFixed(2) };
    });
  };

  const handleChangeTravelBool = (nextValue: boolean) => {
    setTravelBool(nextValue);
  };

  const handleChangeDate = (date: MaterialUiPickersDate) => {
    setDate(date);
  };

  const checkTankError = React.useCallback(
    (value: number, max: number, lastRecord?: number) => {
      return (
        value <= 0 ||
        value > max ||
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (isNumberValid(lastRecord) && !useTravelBool && value > lastRecord!)
      );
    },
    [useTravelBool]
  );

  const checkNonRTUSumError = React.useCallback(() => {
    if (hasRTU || useTravelBool) {
      return false;
    }

    if (
      isNumberValid(installationLastReading) &&
      Object.values(useTankLastReadings).every((x) => x)
    ) {
      const reportedSum = tanks?.reduce((prevResult, tank) => {
        const reportedValue = useTankLastReadings[tank.tank_id];
        return Number(reportedValue) * (tank?.level_conversion_factor ?? 1) + prevResult;
      }, 0);

      return (reportedSum ?? 0) >= (installationLastReading ?? 0);
    }

    return true;
  }, [hasRTU, installationLastReading, tanks, useTankLastReadings, useTravelBool]);

  const checkAnyErrorInTanks = React.useCallback(() => {
    return Boolean(
      tanks?.find((tank) => {
        const currentRegisterValue = useTankLastReadings[tank.tank_id];
        return checkTankError(
          Number(currentRegisterValue),
          tank?.tank_capacity_in ?? 0,
          tank?.tank_level_in
        );
      }) ||
        (!hasRTU && checkNonRTUSumError())
    );
  }, [checkNonRTUSumError, checkTankError, hasRTU, tanks, useTankLastReadings]);

  const checkDateError = React.useCallback(() => {
    return Boolean(!isDateValid(useDate) || (useDate && isAfter(useDate, new Date())));
  }, [useDate]);

  const anyErrorOnForm = React.useMemo(() => {
    return checkAnyErrorInTanks() || checkDateError();
  }, [checkAnyErrorInTanks, checkDateError]);

  const handleClickRegister = async () => {
    setConfirmDialogOpen(false);
    // safe double check
    if (anyErrorOnForm) {
      return;
    }

    setPostLoading(true);

    try {
      const readings: putTankReading[] = Object.keys(useTankLastReadings).map((key) => {
        const value = castNumberTo2Decimals(Number(useTankLastReadings[key]));
        const capacity = tanks?.find((x) => x.tank_id === key)?.tank_capacity_in;
        return {
          tankId: key,
          value,
          capacity,
        };
      });

      const params: InstallationLevelServicePutParams = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        date: useDate!,
        delivery: useTravelBool,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        installationId: installationId!,
        readings: readings,
      };

      await hub.request(POST_INSTALLATION_TANKS_READINGS, params);
      toastr.enqueueSnackbar("Leituras manuais registradas com sucesso!", { variant: "success" });
    } catch (error) {
      toastr.enqueueSnackbar("Ops! Ocorreu um erro ao tentar registrar as leituras manuais!", {
        variant: "error",
      });
    } finally {
      setPostLoading(false);
    }
  };

  const getErrorMessage = React.useCallback(
    (value: string, max: number, min: number, last?: number, altMessages = false) => {
      if (!isNumberValid(value)) {
        return !altMessages ? "Forneça um valor!" : "Sem valor preenchido!";
      }

      if (Number(value) > max) {
        return !altMessages
          ? "O valor não pode ser maior que a capacidade!"
          : "Valor fornecido supera a capacidade máxima!";
      }

      if (Number(value) <= 0) {
        return !altMessages
          ? "O valor não pode ser negativo ou 0!"
          : "Valor fornecido é 0 ou negativo!";
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (isNumberValid(last) && !useTravelBool && Number(value) > last!) {
        return !altMessages
          ? "O valor apenas pode ser maior que a última leitura em caso de carregamento!"
          : "Valor superior à última leitura sem estar indicado que ocorreu carregamento!";
      }

      return undefined;
    },
    [useTravelBool]
  );

  const buildButtonTooltip = React.useCallback(() => {
    const messages: React.ReactNode[] = [];
    if (!anyErrorOnForm) {
      return "";
    }

    if (checkDateError()) {
      messages.push(<div key="date_message">Forneça ou corrija a data!</div>);
    }

    if (!hasRTU && checkNonRTUSumError()) {
      messages.push(
        <div key="non-rtu-error-message">
          A soma dos volumes informados dos tanques supera a última medição disponível! Isso só é
          válido em caso de carregamento.
        </div>
      );
    }

    tanks?.forEach((tank) => {
      const errorMessage = getErrorMessage(
        useTankLastReadings[tank.tank_id],
        tank?.tank_capacity_in ?? 0,
        0,
        tank?.tank_level_in,
        true
      );
      if (errorMessage) {
        const errorComponent = (
          <div key={tank.tank_id}>
            <span>Tanque {tank.tank_id}:</span>
            <span> {errorMessage}</span>
          </div>
        );

        messages.push(errorComponent);
      }
    });

    if (messages.length > 0) {
      return (
        <div>
          <div>
            <strong>Antes de continuar, por favor, corrija os seguintes problemas:</strong>
          </div>
          {messages.map((x) => x)}
        </div>
      );
    }

    return "";
  }, [
    anyErrorOnForm,
    checkDateError,
    checkNonRTUSumError,
    getErrorMessage,
    hasRTU,
    tanks,
    useTankLastReadings,
  ]);

  return (
    <>
      <Card>
        <InstallationLevelReportHeader
          date={useDate}
          loadInformation={useTravelBool}
          handleChangeDate={handleChangeDate}
          handleChangeLoadInformation={handleChangeTravelBool}
          disableSendButton={anyErrorOnForm}
          handleClickRegister={
            !hasRTU ? () => handleClickRegister() : () => setConfirmDialogOpen(true)
          }
          buttonLoading={postLoading}
          buttonTooltip={buildButtonTooltip()}
        />

        <Grid className={classes.gridRoot} container>
          {tanks?.map((tank, idx) => {
            const inputValue = get(useTankLastReadings, tank.tank_id, "");
            const valueNumber = Number(inputValue);

            return (
              <Grid key={tank.tank_id || `tank_chart_${idx}`} item xs>
                <InstallationTankLevelReportCard
                  key={tank.tank_id}
                  id={tank.tank_id}
                  rtu={tank.rtu_serial_number}
                  capacity={tank.tank_capacity_su}
                  capacityUom={tank.product_uom}
                  max={tank.tank_capacity_in}
                  maxUom="Pol."
                  lastReadingDate={
                    tank.last_rtu_reading_date ? new Date(tank.last_rtu_reading_date) : undefined
                  }
                  lastReading={tank.tank_level_in}
                  lastReadingUom="Pol."
                  handleChangeInputValue={handleChangeTankReading}
                  inputValue={inputValue}
                  error={checkTankError(
                    valueNumber,
                    tank?.tank_capacity_in ?? 0,
                    tank?.tank_level_in
                  )}
                  inputHelperText={getErrorMessage(
                    inputValue,
                    tank?.tank_capacity_in ?? 0,
                    0,
                    tank?.tank_level_in
                  )}
                />
              </Grid>
            );
          })}
        </Grid>
      </Card>

      <SimpleDialog
        onClose={() => setConfirmDialogOpen(false)}
        draggable={false}
        open={confirmDialogOpen}
        title="Confirmar"
      >
        <DialogContentText className={classes.dialogText}>
          Caro usuário, ao clicar em confirmar, você{" "}
          <strong>confirma estar ciente e se responsabiliza integralmente</strong> da veracidade e
          confiabilidade dos valores manuais aqui preenchidos. Os dados serão adicionados ao banco
          de dados. Deseja continuar?
        </DialogContentText>

        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} variant="text" color="secondary">
            Cancelar
          </Button>
          <Button onClick={() => handleClickRegister()} variant="outlined">
            Confirmar
          </Button>
        </DialogActions>
      </SimpleDialog>
    </>
  );
}

export default InstallationLevelReportTab;
